import { Button } from '@eriko/ui/src/components/ui/button';
import { Loader2 } from 'lucide-react';
import { CreateRuleDialog } from '../../../components/reconciliation/CreateRuleDialog';
import { ManualMatchDialog } from '../../../components/reconciliation/ManualMatchDialog';
import { MatchDetailDrawer } from '../../../components/reconciliation/MatchDetailDrawer';
import { QueueFilterChips } from '../../../components/reconciliation/QueueFilterChips';
import { QueueGroup } from '../../../components/reconciliation/QueueGroup';
import { StatsPanel } from '../../../components/reconciliation/StatsPanel';
import {
  useBulkConfirm,
  useReviewQueue,
  useRunReconciliation,
} from '../../../hooks/useReconciliation';
import { useReconciliationStore } from '../../../stores/reconciliation';

interface Props {
  orgId: string;
}

export function ReconciliationTab({ orgId }: Props) {
  const filter = useReconciliationStore((s) => s.activeFilter);
  const selectedIds = useReconciliationStore((s) => s.selectedMatchIds);
  const clearSelection = useReconciliationStore((s) => s.clearSelection);

  const { data: queue, isLoading, error, refetch } = useReviewQueue(orgId, filter);
  const runEngine = useRunReconciliation(orgId);
  const bulkConfirm = useBulkConfirm(orgId);

  const handleBulkConfirm = async () => {
    if (selectedIds.size === 0) return;
    await bulkConfirm.mutateAsync(Array.from(selectedIds));
    clearSelection();
  };

  const groups = queue?.groups;
  const showGroup = (key: string) =>
    filter === 'all' ||
    (filter === 'suggested' && key === 'suggestedMatches') ||
    (filter === 'unmatched' && key === 'unmatchedTransactions') ||
    (filter === 'auto' && key === 'autoMatched') ||
    (filter === 'missing-doc' && key === 'missingDocuments') ||
    (filter === 'docs-without-payment' && key === 'documentsWithoutPayment') ||
    (filter === 'duplicates' && key === 'possibleDuplicates') ||
    (filter === 'transfers' && key === 'transfers') ||
    (filter === 'ambiguous-vendor' && key === 'ambiguousVendor') ||
    (filter === 'amount-mismatch' && key === 'amountMismatch') ||
    (filter === 'multiple-candidates' && key === 'multipleCandidates');

  return (
    <div className="flex gap-6 h-full min-h-0">
      <aside className="w-[260px] shrink-0">
        <StatsPanel
          counts={queue?.counts}
          onReRun={() => {
            const since = new Date(Date.now() - 30 * 86_400_000).toISOString();
            runEngine.mutate({ scope: 'org-since', since });
          }}
          isRunning={runEngine.isPending}
        />
      </aside>

      <section className="flex-1 min-w-0 flex flex-col">
        <QueueFilterChips counts={queue?.counts} />

        {isLoading ? (
          <div className="flex items-center justify-center py-12 text-surface-500">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading queue…
          </div>
        ) : error ? (
          <div className="py-12 text-center">
            <div className="text-destructive font-medium mb-2">
              Failed to load reconciliation queue
            </div>
            <div className="text-xs text-surface-500 mb-4">
              {error instanceof Error ? error.message : 'Unknown error'}
            </div>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              Retry
            </Button>
          </div>
        ) : !groups ? (
          <div className="py-12 text-center text-surface-500">
            No reconciliation data yet.
          </div>
        ) : (
          <div className="flex-1 overflow-auto space-y-4">
            {showGroup('suggestedMatches') && (
              <QueueGroup
                title="Suggested matches"
                items={groups.suggestedMatches}
              />
            )}
            {showGroup('unmatchedTransactions') && (
              <QueueGroup
                title="Unmatched transactions"
                items={groups.unmatchedTransactions}
              />
            )}
            {showGroup('missingDocuments') && (
              <QueueGroup
                title="Missing documents"
                items={groups.missingDocuments}
              />
            )}
            {showGroup('documentsWithoutPayment') && (groups.documentsWithoutPayment?.length ?? 0) > 0 && (
              <QueueGroup
                title="Docs without payment"
                items={groups.documentsWithoutPayment}
              />
            )}
            {showGroup('ambiguousVendor') && (
              <QueueGroup
                title="Ambiguous vendor"
                items={groups.ambiguousVendor}
              />
            )}
            {showGroup('amountMismatch') && (
              <QueueGroup
                title="Amount mismatch"
                items={groups.amountMismatch}
              />
            )}
            {showGroup('multipleCandidates') && (
              <QueueGroup
                title="Multiple candidates"
                items={groups.multipleCandidates}
              />
            )}
            {showGroup('transfers') && (
              <QueueGroup
                title="Transfers (excluded from P&L)"
                items={groups.transfers}
              />
            )}
            {showGroup('possibleDuplicates') && (
              <QueueGroup
                title="Possible duplicates"
                items={groups.possibleDuplicates}
              />
            )}
            {showGroup('autoMatched') && (
              <QueueGroup
                title="Matched"
                items={groups.autoMatched}
                defaultCollapsed
              />
            )}
          </div>
        )}

        {selectedIds.size > 0 && (
          <div className="sticky bottom-0 mt-4 flex items-center justify-between rounded-md border border-surface-200 bg-surface-0 px-4 py-3 shadow-sm">
            <span className="text-sm text-surface-700">
              {selectedIds.size} match{selectedIds.size === 1 ? '' : 'es'} selected
            </span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={clearSelection}>
                Clear
              </Button>
              <Button
                size="sm"
                onClick={handleBulkConfirm}
                disabled={bulkConfirm.isPending}
              >
                Confirm {selectedIds.size}
              </Button>
            </div>
          </div>
        )}
      </section>

      <MatchDetailDrawer orgId={orgId} />
      <ManualMatchDialog orgId={orgId} />
      <CreateRuleDialog orgId={orgId} />
    </div>
  );
}
